import { Component, Suspense, useEffect, useState, type ReactNode } from "react";
import { PageState } from "./ui/PageState";

function PageLoading({ title }: { title: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const timer = window.setTimeout(() => setSlow(true), 10_000); return () => clearTimeout(timer); }, []);
  return <PageState title={slow ? `${title} 화면 준비 중` : undefined} busy description={slow ? "연결이 지연되고 있습니다. 다른 메뉴로 이동하거나 화면을 다시 불러올 수 있습니다." : undefined}
    retry={slow ? () => window.location.reload() : undefined} retryLabel="화면 다시 불러오기" />;
}

function PageReload({ title }: { title: string }) {
  /* 한 번만 다시 부른다 — 낡은 조각이 아니라 진짜 고장이면 되풀이해 봐야 같은 자리다. */
  useEffect(() => {
    const key = "page-chunk-reloaded";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    window.location.reload();
  }, []);
  return <PageLoading title={title} />;
}

/** Route-family boundary: failed asset loads cannot take down navigation.
 * Retrying reloads the HTML too, so a cached obsolete chunk URL is not reused.
 * A family key preserves the existing page instance when its own tab changes.
 */
export class PageLoadBoundary extends Component<{ title: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    /* 실패 화면을 따로 두지 않는다. 이 오류는 거의 언제나 배포로 낡아 버린 조각을 부르다
       난 것이고, 그때 사람이 할 수 있는 일은 다시 불러오기 하나뿐이다 — 물어보지 않고
       한 번 대신 해 주고, 그동안은 늘 쓰는 로딩만 보인다. 그래도 안 되면 로딩이 10초 뒤
       제 입으로 지연을 말하고 다시 부를 손잡이를 낸다. */
    if (this.state.failed) return <PageReload title={this.props.title} />;
    return <Suspense fallback={<PageLoading title={this.props.title} />}>{this.props.children}</Suspense>;
  }
}
